import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { trasPrimerPintado } from './utils/trasPrimerPintado';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// App Check (anti-abuso): atestigua que las peticiones vienen de la app real.
// Gateado por la env var — sin REACT_APP_RECAPTCHA_SITE_KEY no se inicializa y
// nada cambia. Al activarlo: crear la key reCAPTCHA v3 en Firebase Console →
// App Check, ponerla en .env Y en el secreto FRONTEND_ENV de GitHub, y dejar
// los servicios en modo "monitor" (sin enforce) hasta ver métricas limpias.
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 ANTES DE ENCENDER EL ENFORCEMENT DE APP CHECK, LEER ESTO              ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Esta inicialización espera a que HAYA ALGO EN PANTALLA, y no al importar el
// módulo. El motivo está medido: el script de reCAPTCHA que
// `ReCaptchaV3Provider` descarga pesa 346 KB —el 26% de todo el peso de la carga
// de `/home`— y en 4G lenta tarda ~3,9 s, o sea que seguía bajando mucho después
// de que el contenido debía estar en pantalla, compitiendo por el ancho de banda
// con las peticiones de datos del propio usuario.
//
// ⚠️ EL PRIMER INTENTO USÓ `requestIdleCallback` Y NO SIRVIÓ. Medido contra
// producción: el script seguía bajando ANTES del primer pintado en las 12
// corridas, y en red rápida hasta arrancaba antes que sin diferir (303-444 ms
// contra 433-617). `requestIdleCallback` dice «el hilo está libre», que ocurre
// ANTES de pintar. La señal correcta es `first-contentful-paint`, y vive en
// `src/utils/trasPrimerPintado.js` con su tope: ver allí el porqué de cada
// salida.
//
// NO se quita: las métricas de App Check tienen que seguir recogiéndose (hoy en
// modo MONITOR, ~5% verificadas) porque son la base para decidir el enforcement,
// y el token de depuración del QA sigue funcionando igual (lo lee el SDK al
// inicializar, no al importar).
//
// ⚠️ QUÉ CAMBIA PARA LAS PETICIONES QUE SALEN ANTES DE QUE ESTO CORRA.
// Verificado leyendo los SDK instalados, no supuesto:
//   · Firestore (`@firebase/firestore`, `FirebaseAppCheckTokenProvider`): si App
//     Check todavía no está registrado, `getToken()` devuelve `null` y la
//     petición sale SIN cabecera de atestación — registra en depuración
//     "AppCheck not yet detected" y sigue. NO encola ni reintenta. Se suscribe
//     con `onInit`, así que en cuanto App Check arranca, las peticiones
//     SIGUIENTES sí van atestadas.
//   · Functions (`@firebase/functions`, `ContextProvider`): igual — si
//     `this.appCheck` es null, `getAppCheckToken()` devuelve `null` y la cabecera
//     `X-Firebase-AppCheck` no se manda. Tampoco encola.
//   · Storage: mismo patrón (`addAppCheckHeader_` omite la cabecera si el token
//     es null).
//   · Nuestro propio API (`src/api/client.js`) NO manda token de App Check por
//     ningún camino: el enforcement de App Check no lo toca.
// O sea: HOY (monitor) no pasa nada, solo se cuentan como no verificadas las
// peticiones de los primeros segundos. CON ENFORCEMENT ENCENDIDO esas peticiones
// tempranas serían RECHAZADAS y nadie las reintentaría.
//
// 🔴 Y LA VENTANA SIN ATESTAR SE ALARGÓ CON ESTE CAMBIO, con números medidos:
// antes App Check arrancaba al importar el módulo (~0 ms); con la señal de
// pintado arranca después del primer pintado, que en las corridas contra
// producción cae en **0,3-0,45 s en red rápida y hasta ~2,5 s en 4G lenta**, y
// como tope duro 10 s (`TOPE_MS`). Todo lo que la app pida en esa ventana sale
// sin atestar. Con el enforcement encendido, eso NO es «una métrica peor»: es
// el arranque entero rechazado.
//
// Por eso, antes de encender el enforcement hay que decidir una de dos:
//   (a) volver a inicializar App Check en línea (revertir este bloque), asumiendo
//       de nuevo los ~346 KB en la ruta crítica — y sabiendo que entonces las
//       primeras lecturas de Firestore ESPERAN al intercambio de reCAPTCHA; o
//   (b) mantener el diferido y asegurarse de que ninguna lectura/escritura salga
//       antes: p. ej. esperar a que App Check esté listo en el arranque de las
//       pantallas que piden datos.
// Lo que NO se puede hacer es encender el enforcement y dar por hecho que el SDK
// las reintenta: no lo hace.
function iniciarAppCheck() {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.error('App Check init error:', e);
  }
}

if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
  // `esperar: 'pintado'` y NO `'carga'`, a propósito. `load` alejaría más los
  // 346 KB del contenido (espera a TODOS los recursos, fotos de vehículos
  // incluidas), pero cada segundo que App Check tarda en arrancar es un tramo
  // más del arranque que se cuenta como NO verificado — y esa métrica es
  // justamente con la que el dueño va a decidir el enforcement. Retrasarla más
  // no mejoraría la app: envenenaría el dato con el que se decide.
  trasPrimerPintado(iniciarAppCheck);
}

export const auth = getAuth(app);
// Firestore con caché offline persistente (IndexedDB) — datos ya vistos disponibles sin conexión.
// Multi-pestaña seguro. Reemplaza getFirestore(app).
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  // Auto-detecta redes donde el WebChannel de streaming falla (proxy/antivirus/firewall
  // que interceptan TLS) y usa long-polling desde el inicio. Elimina los ERR_FAILED y
  // acelera el cold-start de conexión en redes restringidas.
  experimentalAutoDetectLongPolling: true,
});
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');